import { useState } from "react";
import { PANEL, TEAL, CREAM, CREAM60, CREAM30, BORDER, AMBER, GREEN, NAVY } from "../utils/constants";

/**
 * Conversational role refinement.
 *
 * Automated resolution has a ceiling. A 22-year technology delivery leader whose
 * title reads "Associate Director" cannot be resolved from text alone with any
 * confidence — and asserting a guess destroys trust in everything downstream.
 *
 * So the user teaches the system, in their own words, and watches the ranking
 * move. This is ADR-025 rung 5 done properly: not a form, a conversation, and
 * the system shows WHY each candidate matched so the user can see what to correct.
 */

const PROMPTS = [
  "What do you actually spend most of your week doing?",
  "What decisions are yours to make?",
  "What would break if you stopped doing your job for a month?",
];

/**
 * `compact` is set once the activity refinement has run. The candidate list and
 * the confidence framing already appear in that panel, so repeating them here
 * reads as though nothing happened — the same five roles under "Help us get
 * this right", directly beneath a ledger saying the match just improved.
 *
 * What still earns its place is the free-text escape hatch, because it answers
 * a different question: not "which of these", but "none of these".
 */
export default function RoleRefiner({ matches, via, confidence, chosen, onChoose, onRefine, refining, compact }) {
  const [text, setText] = useState("");
  const [prompt] = useState(() => PROMPTS[0]);

  const low = confidence < 0.7;

  return (
    <div style={{ background: PANEL, border: `1px solid ${low ? AMBER : BORDER}40`,
                  borderRadius: 12, padding: 20, marginBottom: 16 }}>
      {!compact && (
        <>
          <div style={{ fontSize: 14, fontWeight: 700, color: CREAM, marginBottom: 4 }}>
            {low ? "Help us get this right" : "Is this right?"}
          </div>
          <div style={{ fontSize: 12, color: CREAM30, marginBottom: 14, lineHeight: 1.5 }}>
            {via === "context"
              ? "Your job title is a rank, not a role — so we read your history and what you described instead. Titles like “Associate Director” tell us nothing on their own."
              : "We matched this from your profile. Pick a better fit if one of these is closer."}
          </div>
        </>
      )}

      <div style={{ display: compact ? "none" : "grid", gap: 8, marginBottom: 16 }}>
        {matches.slice(0, 5).map((m) => {
          const active = chosen === m.code;
          return (
            <button key={m.code} onClick={() => onChoose(m.code)}
              style={{ textAlign: "left", background: active ? `${TEAL}18` : "transparent",
                       border: `1px solid ${active ? TEAL : CREAM30}`, borderRadius: 8,
                       padding: "12px 14px", cursor: "pointer" }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                <span style={{ color: CREAM, fontSize: 13, fontWeight: 600 }}>{m.title}</span>
                <span style={{ color: active ? TEAL : CREAM30, fontSize: 11,
                               fontFamily: "'JetBrains Mono', monospace" }}>
                  {Math.round(m.confidence * 100)}%
                </span>
              </div>
              {m.why?.length > 0 && (
                <div style={{ fontSize: 11, color: CREAM30, marginTop: 4 }}>
                  matched on: {m.why.join(", ")}
                </div>
              )}
            </button>
          );
        })}
      </div>

      <div style={{ borderTop: compact ? "none" : `1px solid ${BORDER}`, paddingTop: compact ? 0 : 14 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: CREAM, marginBottom: 2 }}>
          {compact ? "Still not right? Tell us in your own words." : "None of these? Tell us more."}
        </div>
        <div style={{ fontSize: 12, color: CREAM30, marginBottom: 10 }}>{prompt}</div>
        <textarea
          rows={3} value={text} onChange={(e) => setText(e.target.value)}
          placeholder="e.g. I run delivery for a portfolio of cloud migration projects — I own scope, budget and the client relationship, and I manage a team of project managers."
          style={{ width: "100%", background: NAVY, border: `1px solid ${CREAM30}`, borderRadius: 8,
                   padding: "10px 12px", color: CREAM, fontSize: 13, outline: "none",
                   fontFamily: "'Space Grotesk', sans-serif", resize: "vertical", marginBottom: 10 }}
        />
        <button
          onClick={() => { if (text.trim()) onRefine(text.trim()); }}
          disabled={!text.trim() || refining}
          style={{ background: text.trim() ? `${TEAL}20` : "transparent",
                   border: `1px solid ${text.trim() ? TEAL : CREAM30}`,
                   color: text.trim() ? TEAL : CREAM30, padding: "9px 18px", borderRadius: 8,
                   cursor: text.trim() ? "pointer" : "default", fontSize: 13, fontWeight: 600 }}>
          {refining ? "Re-reading…" : "Refine the match →"}
        </button>
        <div style={{ fontSize: 11, color: CREAM30, marginTop: 10, lineHeight: 1.5 }}>
          Each time you add context we re-rank against 1,016 occupations. Nothing you type
          leaves your device at this step.
        </div>
      </div>
    </div>
  );
}
